// @workflow assistant.ui-tools
/**
 * Buttons are actions (Khalid, 2026-10-14): every chip under a reply ("Open the tag", "Show in 3D",
 * "Open in 360", a page) does its thing in the app, it never only changes the URL. A chip is
 * `{ label, icon?, action?: { name, args }, to? }` and resolves, in this order, to:
 *   1. its `action`, when it is one of this build's UI actions (uiTools.js, never a query);
 *   2. the action its tag deep link stands for (linkIntent.js, the temporary bridge);
 *   3. its app route (`to`, toAppLocation): real pages still navigate;
 *   else nothing: the chip is not shown.
 * An action this build doesn't know falls through to the chip's link, so an older UI still works.
 * `to` is kept for copying and sharing (replyView.copyTextFor).
 * No Vue or path-alias imports, so `npm run test:unit` (node --test) can load it directly.
 */
import { toAppLocation } from './markdown.js';
import { linkActionOf } from './linkIntent.js';
import { UI_TOOL, isUiActionName } from './uiTools.js';
import { VIEWER_DETAIL } from './viewerContract.js';
import { TAG_DETAIL } from './tagTools.js';

export const CHIP_KIND = Object.freeze({ ACTION: 'action', ROUTE: 'route' });

/** What the user reads when a button failed and the tool gave no words of its own. */
export const CHIP_FAILED_DETAIL = 'AVA could not do that. Try again.';

const ACTION_KEY_PREFIX = 'action:';

/**
 * The tools' details are written for the model ("Ask the user to…", "(facilityId)"). The person who
 * clicked reads these instead; every other detail already reads well to them.
 */
const USER_WORDS = Object.freeze({
  [VIEWER_DETAIL.NO_ROOM]: 'All the views on the page are in use. Close one, then try again.',
  [VIEWER_DETAIL.NO_FACILITY]: 'Open a facility first, then try again.',
  [VIEWER_DETAIL.OTHER_FACILITY]: 'This tag is in another facility.',
  [VIEWER_DETAIL.LEFT_PAGE]: 'You left the facility before it opened.',
});

/** Results that are the user's own choice, not a failure: no toast. */
const USERS_CHOICE = Object.freeze([TAG_DETAIL.DECLINED]);

const isPlainObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);
const isAbsent = (value) => value === undefined || value === null;

/** A runnable copy of the chip's own action, or null. */
function ownAction(action) {
  if (!isPlainObject(action) || !isUiActionName(action.name)) return null;
  if (!isAbsent(action.args) && !isPlainObject(action.args)) return null;
  return { name: action.name, args: isAbsent(action.args) ? undefined : { ...action.args } };
}

/**
 * What clicking the chip does.
 * @param {{ label?: string, action?: { name: string, args?: object }, to?: string|object }|null} link
 * @returns {{ kind: 'action', action: { name: string, args?: object } } | { kind: 'route', to: string|object } | null}
 */
export function chipTarget(link) {
  if (!isPlainObject(link)) return null;
  const action = ownAction(link.action);
  if (action) return { kind: CHIP_KIND.ACTION, action };
  const to = toAppLocation(link.to);
  if (!to) return null;
  const bridged = linkActionOf(to);
  return bridged ? { kind: CHIP_KIND.ACTION, action: bridged } : { kind: CHIP_KIND.ROUTE, to };
}

/** The UI action a resolved chip runs: its action, or `ui.navigate` to its route. One path for every button. */
export const chipActionOf = (target) => (target.kind === CHIP_KIND.ACTION ? target.action : { name: UI_TOOL.NAVIGATE, args: { to: target.to } });

/** Chips that do the same thing share a key (an action and the deep link it came from too). */
export function chipKey(link) {
  const target = chipTarget(link);
  if (target && target.kind === CHIP_KIND.ACTION) return `${ACTION_KEY_PREFIX}${JSON.stringify(target.action)}`;
  const to = link && link.to;
  return typeof to === 'string' ? to : JSON.stringify(to);
}

/**
 * The toast for a button's result: the tool's plain `detail` in the user's words, or the named
 * fallback; null on success and when the user chose it (declined the nearest 360 image).
 * @param {{ ok: boolean, detail?: string }|null} result
 * @returns {string|null}
 */
export function chipFailureText(result) {
  if (result && result.ok) return null;
  const detail = result && typeof result.detail === 'string' ? result.detail : '';
  if (USERS_CHOICE.includes(detail)) return null;
  if (Object.prototype.hasOwnProperty.call(USER_WORDS, detail)) return USER_WORDS[detail];
  return detail || CHIP_FAILED_DETAIL;
}
